<template>
  <div class="fallback-container">
    <!-- Pantalla para In-App Browsers (Instagram, TikTok, Facebook) -->
    <div
      v-if="isInAppBrowser"
      class="hero-screen"
    >
      <!-- Background image with overlay -->
      <div class="hero-bg">
        <img
          src="/hero-bg.png"
          alt=""
          class="hero-bg__img"
        />
        <div class="hero-bg__overlay" />
      </div>

      <!-- Top navbar -->
      <nav class="hero-nav">
        <img
          src="/fanaty-logo-purple.png"
          alt="Fanaty"
          class="hero-nav__logo"
        />
      </nav>

      <!-- Hero content -->
      <div class="hero-content">
        <h1 class="hero-title">
          <span class="hero-title__white">Juega contra equipos locales.</span>
          <span class="hero-title__purple">Compite en todo el mundo.</span>
        </h1>

        <p class="hero-description">
          Ganá partidos para sumar puntos en la Fanaty World
          League. Cada juego es grabado por nuestras cámaras
          con inteligencia artificial.
        </p>

        <button
          class="hero-btn"
          @click="openApp"
        >
          Ver en Fanaty app
        </button>
      </div>
    </div>

    <!-- Pantalla de redirección normal -->
    <div
      v-else
      class="loading-screen"
    >
      <img
        src="https://console.fanaty.com/img/logo-2.269da70f.png"
        alt="Fanaty"
        class="logo-sm"
      />
      <div class="spinner" />
      <span class="loading-text">Redirigiendo...</span>
    </div>
  </div>
</template>

<script>

export default {
  name: 'FallbackLinks',

  data() {
    return {
      isInAppBrowser: false,
      isIOS: false,
      isAndroid: false,
    }
  },

  computed: {
    // URL de la página intermedia que se abre en Chrome/Safari (fuera del WebView de Instagram)
    redirectPageUrl() {
      const { deepLink } = this.$route.query
      const deepLinkParam = deepLink || this.$route.fullPath

      return `https://app.fanaty.com/deep-link-redirect?deepLink=${encodeURIComponent(deepLinkParam)}`
    },
  },

  beforeCreate() {
    const ua = navigator.userAgent || navigator.vendor || window.opera

    const isInstagram = /Instagram/i.test(ua)
    const isTikTok = /TikTok|musical_ly|BytedanceWebview/i.test(ua)
    const isFacebook = /FBAN|FBAV|FB_IAB|FacebookExternalHit/i.test(ua)

    const isInApp = isInstagram || isTikTok || isFacebook

    if (!isInApp) {
      // Comportamiento original: redirigir al store
      const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream
      const isAndroid = /android/i.test(ua)

      const appStoreUrl = 'itms-apps://apps.apple.com/us/app/fanaty/id1461051554'
      const playStoreUrl = 'market://details?id=com.fanaty'
      const webUrl = 'https://www.fanaty.com/'

      if (isIOS) {
        window.location.replace(appStoreUrl)
      } else if (isAndroid) {
        window.location.replace(playStoreUrl)
      } else {
        window.location.replace(webUrl)
      }
    }
  },

  created() {
    const ua = navigator.userAgent || navigator.vendor || window.opera

    const isInstagram = /Instagram/i.test(ua)
    const isTikTok = /TikTok|musical_ly|BytedanceWebview/i.test(ua)
    const isFacebook = /FBAN|FBAV|FB_IAB|FacebookExternalHit/i.test(ua)

    this.isInAppBrowser = isInstagram || isTikTok || isFacebook
    this.isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream
    this.isAndroid = /android/i.test(ua)
  },

  methods: {
    openApp() {
      if (this.isIOS) {
        // iOS: window.open está bloqueado en WebViews de Instagram/TikTok/Facebook.
        // Intentar abrir la app directamente via Universal Link.
        const { deepLink } = this.$route.query
        const deepLinkParam = deepLink || this.$route.fullPath

        // Limpiar parámetros fbclid que Instagram agrega automáticamente
        const [cleanPath] = deepLinkParam.split('?')
        const deepLinkUrl = `https://app.fanaty.com${cleanPath}`

        window.location.href = deepLinkUrl

        // Si la app no está instalada, después de 2s ir al App Store
        setTimeout(() => {
          window.location.href = 'itms-apps://apps.apple.com/us/app/fanaty/id1461051554'
        }, 2000)
      } else {
        // Android: window.open funciona en la mayoría de WebViews
        // y abre Chrome, donde intent:// puede resolver la app.
        window.open(this.redirectPageUrl, '_blank', 'noopener,noreferrer')
      }
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap');

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.fallback-container {
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #0a0a14;
}

/* ── Hero Screen (In-App Browser) ── */
.hero-screen {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Background image */
.hero-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.hero-bg__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.hero-bg__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(10, 10, 20, 0.45) 0%,
    rgba(10, 10, 20, 0.6) 40%,
    rgba(10, 10, 20, 0.85) 70%,
    rgba(10, 10, 20, 0.95) 100%
  );
}

/* Navbar */
.hero-nav {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  padding: 20px 24px;
}

.hero-nav__logo {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

/* Hero Content */
.hero-content {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 24px 60px;
  max-width: 720px;
}

.hero-title {
  font-size: 48px;
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -1.5px;
  margin-bottom: 24px;
}

.hero-title__white {
  display: block;
  color: #ffffff;
}

.hero-title__purple {
  display: block;
  color: #8b5cf6;
}

.hero-description {
  font-size: 16px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  max-width: 480px;
  margin-bottom: 32px;
}

.hero-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 16px 40px;
  background: #8b5cf6;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  letter-spacing: 0.3px;
  width: fit-content;
  box-shadow: 0 4px 24px rgba(139, 92, 246, 0.4);
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.2s ease;
}

.hero-btn:hover {
  background: #7c3aed;
  box-shadow: 0 6px 32px rgba(139, 92, 246, 0.5);
}

.hero-btn:active {
  transform: scale(0.97);
  box-shadow: 0 2px 16px rgba(139, 92, 246, 0.35);
}

/* ── Loading Screen ── */
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 20px;
}

.logo-sm {
  width: 80px;
  object-fit: contain;
  opacity: 0.9;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #8b5cf6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.3px;
}

/* ── Responsive ── */
@media (max-width: 480px) {
  .hero-title {
    font-size: 36px;
    letter-spacing: -1px;
  }

  .hero-description {
    font-size: 15px;
  }

  .hero-btn {
    width: 100%;
  }
}
</style>
